import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Slide, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { BallTriangle, MoonLoader } from "react-spinners";
import { useCookies } from "react-cookie";

function UploadEmployeeExitsDocument() {
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isUpdateFormVisible, setIsUpdateFormVisible] = useState(false);
  const [status, setStatus] = useState("Pending");
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [cookies, setCookie, removeCookie] = useCookies([
    "authToken",
    "userName",
    "role",
    "team",
  ]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [showDownloadOptionss, setShowDownloadOptionss] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [uploadStatus, setUploadStatus] = useState({
    Salary_Slip: "This document is not uploaded.",
    Experience_Letter: "This document is not uploaded.",
    Form16: "This document is not uploaded.",
  });
  const dropdownRef = useRef(null);

  const schema = yup.object().shape({
    FNF_Status: yup.mixed().required("FNF Status is required"),
    Salary_Slip: yup.mixed().required("Salary Slip is required"),
    Experience_Letter: yup.mixed().required("Experience Letter is required"),
    Form16: yup.mixed().required("Form 16 is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch data from the API
      const response = await fetch(
        `http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/CreateEmployeeExitsAccount?page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(
          debouncedQuery
        )}`
      );

      // Check if the response is okay
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData?.message || "Failed to retrieve data");
        return;
      }

      // If the response is successful, parse the response data
      const responseData = await response.json();
      if (responseData?.data && responseData?.data.length > 0) {
        setData(responseData?.data);
        setFilteredDocuments(responseData?.data);
        setTotalPages(responseData?.totalPages);
        setError(""); // Clear any previous error message if data is found
      } else {
        // If no data is found, show a user-friendly error message
        // setData([]);
        setFilteredDocuments([]);
        setError("User not found.");
      }
    } catch (error) {
      // Handle any errors that occur during the fetch request
      console.error("Error:", error);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, debouncedQuery]);

  const onSubmit = async (formData) => {
    const fileData = new FormData();
    let hasFileUploaded = false;

    Object.entries(formData).forEach(([key, value]) => {
      if (value[0]) {
        fileData?.append(key, value[0]);
        hasFileUploaded = true;
      }
    });

    if (selectedAccount) {
      fileData?.append("userid", selectedAccount._id);
    } else {
      toast.error("No account selected.");
      return;
    }

    if (!hasFileUploaded) {
      toast.error("No files selected for upload.");
      return;
    }

    try {
      const response = await fetch(
        "http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/uploadExitEmployeeDoc",
        {
          method: "POST",
          body: fileData,
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const newUploadStatus = { ...uploadStatus };
      for (const key in formData) {
        if (formData[key][0]) {
          newUploadStatus[key] = "This document is uploaded!";
        }
      }

      localStorage.setItem("uploadStatus", JSON.stringify(newUploadStatus));
      setUploadStatus(newUploadStatus);
      toast.success("Documents Upload Successful", {
        position: "top-center",
        transition: Slide,
      });
      reset();
      setIsFormVisible(false);
    } catch (error) {
      toast.error("Error submitting form. Please try again.");
      console.error("Error:", error);
    }
  };

  const handleRowClick = (account) => {
    setSelectedAccount(account);
    setIsFormVisible(true);
  };

  const handleCloseForm = () => {
    setIsFormVisible(false);
    setSelectedAccount(null);
  };

  const handleUpdate = async (updatedData) => {
    if (!selectedAccount) {
      toast.error("No account selected for update.");
      return;
    }

    try {
      const response = await fetch(
        `http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/UpdateEmployeeExitsAccount/${selectedAccount._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updatedData),
        }
      );

      const responseData = await response.json();

      if (response.ok) {
        toast.success("Employee account updated successfully!");
        setIsUpdateFormVisible(false);
        setSelectedAccount(null);
        fetchData();
      } else {
        toast.error(responseData.message || "Failed to update account.");
      }
    } catch (error) {
      toast.error("Error updating account. Please try again.");
      console.error("Error:", error);
    }
  };

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Employee Data");
    XLSX.writeFile(wb, "employee_data.xlsx");
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Employee Exit Data", 10, 10);
    const columns = [
      "Username",
      "Employee ID",
      "Last Working Day",
      "Email",
      "Created By",
      "Status",
    ];
    const rows = data.map((account) => [
      account.username,
      account.EmployeeID,
      account.Date_of_Release,
      account.email,
      account.hrUsername,
      account.status,
    ]);
    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
    });
    doc.save("employee_data.pdf");
  };

  const handleUserDelete = async (userId) => {
    try {
      if (window.confirm("Are you sure you want to delete this user ?")) {
        const response = await fetch(
          `http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/ExitsDocuments/User/${userId}`,
          {
            method: "DELETE",
          }
        );

        const data = await response.json();
        if (!response.ok)
          throw new Error(data.message || "Failed to delete user");

        alert("User deleted successfully");
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting user:", error.message);
      alert("Failed to delete user. Please try again.");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const togglePasswordVisibility = (index) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleUpdateClick = (account) => {
    setSelectedAccount(account);
    setIsUpdateFormVisible(true);
  };

  const handleUsernameClick = (id) => {
    navigate(`/ExitCandidate/${id}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDownloadOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 1250); // 500ms debounce

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  return (
    <div className="mb-5">
      <ToastContainer />

      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <MoonLoader color="#00BFFF" height={100} width={100} />
        </div>
      ) : (
        <div>
          {data?.length > 0 && (
            <div className="mt-4 p-4  border-gray-300 rounded">
              <div className=" flex juestify-between">
                <div className="search-bar">
                  <input
                    type="text"
                    value={searchQuery}
                    autoFocus
                    onChange={handleSearch}
                    placeholder="Search by username, email or employee ID"
                    className="w-full px-4 py-2 border border-black rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <th
                  className="relative px-2 py-2 w-full   text-right flex justify-end"
                  ref={dropdownRef}
                >
                  <button
                    onClick={() => setShowDownloadOptions(!showDownloadOptions)}
                    className="font-medium border p-2 rounded-full bg-blue-500 text-white"
                  >
                    Download Details 
                  </button>

                  {showDownloadOptions && (
                    <div className="absolute right-0 mt-2 bg-white shadow-lg border rounded w-48 z-50">
                      <button
                        onClick={handleDownloadExcel}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 border-b"
                      >
                        Download in Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      >
                        Download in PDF
                      </button>
                    </div>
                  )}
                </th>
              </div>

              <div className="pagination flex items-center justify-between space-x-4 mt-6">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Previous
                </button>
                <span className="text-sm ">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                </button>
              </div>

              <table className="min-w-full border border-gray-300 mt-2">
                <thead>
                  <tr>
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Username
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Employee ID
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Date of Release
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Email
                    </th>
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Password
                    </th>

                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Created By{" "}
                    </th>
                    {cookies.role !== "67f7d873fa852258bf9a2112" && (
                      <th className="border border-gray-300 px-2 py-2 text-sm  justify-center text-center">
                        {" "}
                        Actions
                      </th>
                    )}
                    <th className="border border-gray-300 px-2 py-2 text-sm text-center">
                      Status
                    </th>

                    
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments?.map((account, index) => (
                    <tr
                      key={index}
                      className="cursor-pointer hover:bg-gray-200"
                    >
                      <td
                        className="border border-gray-300 px-2 py-2 text-sm text-center"
                        onClick={() => handleUsernameClick(account._id)}
                      >
                        {account.username}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                        {account.EmployeeID}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                        {account.Date_of_Release}
                      </td>
                      <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                        {account.email}
                      </td>
                      <td
                        className="border border-gray-300 px-2 py-2 text-sm text-center cursor-pointer"
                        onClick={() => togglePasswordVisibility(index)}
                      >
                        {visiblePasswords[index] ? account.password : "******"}
                      </td>

                      <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                        {decodeURIComponent(account.hrUsername)}
                      </td>
                      {cookies.role !== "67f7d873fa852258bf9a2112" && (
                        <td className="border border-gray-300 px-2 py-2 flex text-sm justify-center">
                          <div className="border border-gray-300 px-2 py-2 flex text-sm ">
                            <button
                              onClick={() => handleRowClick(account)}
                              className=" mx-2 text-blue-500 text-sm"
                            >
                              Upload
                            </button>
                          </div>

                          <td className="border border-gray-300 px-2 py-2 flex text-sm mx-2">
                            {" "}
                            <button
                              onClick={() => handleUpdateClick(account)}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              Edit
                            </button>
                          </td>

                          <div className="border border-gray-300 px-2 py-2 flex text-sm mx-2">
                            <button
                              onClick={() => handleUserDelete(account._id)}
                              className=" mx-2 text-red-600 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                      <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                        {account.status}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {isFormVisible && selectedAccount && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-white bg-opacity-50">
              <div className="bg-white p-6 mx-20 rounded-lg shadow-lg w-1/2 px-3">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className=" flex justify-between">
                    <h3 className="font-bold text-2xl">
                      Upload Documents for {selectedAccount.username}
                    </h3>
                    <button
                      onClick={handleCloseForm}
                      className=" text-red-500 hover:text-red-700 text-4xl"
                      aria-label="Close"
                    >
                      &times;
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                    {/* FNF Status Section */}
                    <div className="p-2 rounded-lg mb-2">
                      <label
                        htmlFor="FNF_Status"
                        className="block text-sm font-medium"
                      >
                        FNF Status
                      </label>
                      <select
                        id="FNF_Status"
                        {...register("FNF_Status")}
                        className="block w-full px-4 py-2 mt-2 border rounded-lg"
                      >
                        <option value="Pending">Pending</option>
                        <option value="Completed">Completed</option>
                        <option value="In Progress">In Progress</option>
                      </select>
                      {errors.FNF_Status && (
                        <span className="text-red-500 text-sm">
                          {errors.FNF_Status.message}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="Salary_Slip"
                        className="block text-sm font-serif text-black font-bold"
                      >
                        Salary Slips (For last 3 Months):
                      </label>
                      <input
                        type="file"
                        id="Salary_Slip"
                        {...register("Salary_Slip")}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                      {errors.Salary_Slip && (
                        <p className="text-red-500 text-xs italic">
                          {errors.Salary_Slip.message}
                        </p>
                      )}
                      
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor="Experience_Letter"
                        className="block text-sm font-serif text-black font-bold"
                      >
                        Relieving Letter cum Experience Certificate:
                      </label>
                      <input
                        type="file"
                        id="Experience_Letter"
                        {...register("Experience_Letter")}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                      {errors.Experience_Letter && (
                        <p className="text-red-500 text-xs italic">
                          {errors.Experience_Letter.message}
                        </p>
                      )}
                     
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor="Form16"
                        className="block text-sm font-serif text-black font-bold"
                      >
                        Form 16:
                      </label>
                      <input
                        type="file"
                        id="Form16"
                        {...register("Form16")}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                      {errors.Form16 && (
                        <p className="text-red-500 text-xs italic">
                          {errors.Form16.message}
                        </p>
                      )}
                      
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="ExitStatement"
                        className="block text-sm font-serif text-black font-bold"
                      >
                        Exit Statement:
                      </label>
                      <input
                        type="file"
                        id="ExitStatement"
                        {...register("ExitStatement")}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                      {errors.ExitStatement && (
                        <p className="text-red-500 text-xs italic">
                          {errors.ExitStatement.message}
                        </p>
                      )}
                      
                    </div>

                   
                  </div>
                  <button
                    type="submit"
                    className="mt-4 p-2 bg-blue-500 text-white rounded hover:bg-blue-700"
                  >
                    Upload Documents
                  </button>
                </form>
              </div>
            </div>
          )}

          {isUpdateFormVisible && selectedAccount && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-white bg-opacity-50">
              <div className="bg-white p-6 mx-20 rounded-lg shadow-lg w-1/2 px-3">
                <h3 className="font-bold text-2xl">
                  Update Details for {selectedAccount.username}
                </h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const updatedData = {
                      username: e.target.username.value,
                      EmployeeID: e.target.EmployeeID.value,
                      Date_of_Release: e.target.Date_of_Release.value,
                      email: e.target.email.value,
                      password: e.target.password.value,
                      hrUsername: e.target.hrUsername.value,
                    };
                    handleUpdate(updatedData);
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="form-group">
                      <label
                        htmlFor="username"
                        className="block text-sm font-semibold"
                      >
                        Username:
                      </label>
                      <input
                        type="text"
                        id="username"
                        defaultValue={selectedAccount.username}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold"
                      >
                        Employee ID:
                      </label>
                      <input
                        type="text"
                        id="EmployeeID"
                        defaultValue={selectedAccount.EmployeeID}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold"
                      >
                        Last Working Day:
                      </label>
                      <input
                        type="date"
                        id="Date_of_Release"
                        defaultValue={selectedAccount.Date_of_Release}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold"
                      >
                        Email:
                      </label>
                      <input
                        type="email"
                        id="email"
                        defaultValue={selectedAccount.email}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="password"
                        className="block text-sm font-semibold"
                      >
                        Password:
                      </label>
                      <input
                        type="password"
                        id="password"
                        defaultValue={selectedAccount.password}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div className="form-group">
                      <label
                        htmlFor="hrUsername"
                        className="block text-sm font-semibold"
                      >
                        HR Username:
                      </label>
                      <input
                        type="text"
                        id="hrUsername"
                        defaultValue={selectedAccount.hrUsername}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-4 p-2 bg-green-500 text-white rounded hover:bg-green-700"
                  >
                    Update Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsUpdateFormVisible(false)}
                    className="mt-4 ml-4 p-2 bg-red-500 text-white rounded hover:bg-red-700"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UploadEmployeeExitsDocument;
